import React, { memo, useEffect, useMemo, useRef, RefObject, useImperativeHandle, MouseEventHandler } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import rrulePlugin from '@fullcalendar/rrule';
import interactionPlugin, { Draggable, DateClickArg } from '@fullcalendar/interaction';
import BootstrapTheme from '@fullcalendar/bootstrap';
import {
  CalendarOptions,
  EventInput,
  CalendarApi,
  EventClickArg,
  DatePointApi,
  ViewApi,
  EventContentArg
} from '@fullcalendar/core';
import { toMoment } from '@core/utils/time';
import { Moment } from 'moment';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import momentPlugin from '@fullcalendar/moment';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { showAlert } from "@modules/common";
import { TaskChecklist } from '../SystemCalendar/TaskChecklist/TaskChecklist';

export enum CalendarView {
  month = 'dayGridMonth',
  week = 'timeGridWeek',
  day = 'timeGridDay',
  resource = 'resourceTimeGridDay',
  listDay = 'listDay',
  listWeek = 'listWeek',
  listMonth = 'listMonth',
}

export interface CalendarEvent<T = any> extends Omit<EventInput, 'start' | 'end'> {
  start: number;
  end?: number;
  item: T;
}

export interface CalendarHandle {
  calendar: () => CalendarApi | null;
}

export interface CalendarProps<T> extends Omit<CalendarOptions, 'events'> {
  events: CalendarEvent<T>[];
  onDayClick?: (date: Moment, event: DateClickArg) => void;
  onSelect?: (start: Moment, end: Moment, allDay: boolean, view: string) => void;
  onEventClick?: (id: string, item: T, event: EventClickArg) => void;
  onEventChange?: (id: string, item: T, start: number, end?: number) => void;
  types?: CalendarView[];
  handlesRef?: RefObject<CalendarHandle>;
  renderEventContent?: (event: EventContentArg) => void;
  readOnly?: boolean
}

export const Calendar = memo(<T extends any>(props: CalendarProps<T | any>) => {
  const { onSelect, onDayClick, onEventClick, onEventChange, events, types, renderEventContent, handlesRef, readOnly = false, ...originalProps } = props;
  const { initialView: extractedInitialView, ...rest } = originalProps;
  const initialView = extractedInitialView || CalendarView.month;
  const ref = React.useRef<FullCalendar | null>();

  useImperativeHandle(props.handlesRef, () => ({
    calendar: () => (ref.current ? ref.current!.getApi() : null),
  }));

  const calEvents = useMemo(() => {
    return events.map(e => ({ ...e, start: toMoment(e.start).toDate(), end: e.end ? toMoment(e.end).toDate() : undefined, extendedProps: e.item }));
  }, [events]);

  const calendar = (
    <FullCalendar
      scrollTimeReset={false}
      schedulerLicenseKey="0031686291-fcs-1698073537"
      ref={ref as any}
      initialView={initialView}
      plugins={[momentPlugin, BootstrapTheme, dayGridPlugin, rrulePlugin, interactionPlugin, resourceTimeGridPlugin, timeGridPlugin, listPlugin]}
      buttonText={{
        listMonth: 'month',
        listWeek: 'week',
        listDay: 'day',
      }}
      slotDuration={'00:15:00'}
      handleWindowResize={true}
      themeSystem="bootstrap"
      headerToolbar={{
        left: 'prev,next today',
        center: 'title',
        right: types ? (types.length > 1 ? types.join(',') : undefined) : 'dayGridMonth,dayGridWeek,dayGridDay',
      }}
      eventDisplay="block"
      events={calEvents}
      eventBorderColor="transparent"
      editable={!readOnly}
      allDaySlot={false}
      eventStartEditable={!readOnly}
      droppable={!readOnly}
      selectable={!readOnly}
      select={readOnly ? undefined : (event) => {
        const isWeekView = event.view.type === 'timeGridWeek';
        const isMonthView = event.view.type === 'dayGridMonth';
        const start = toMoment(event.start);
        const end = isMonthView ? toMoment(event.end).subtract(1410, 'minutes') : toMoment(event.end);

        if (isWeekView && !start.isSame(end, 'day')) {
          showAlert('Multi-day Event', 'Multi-day selection is not allowed in week view.')
          return;
        }

        onSelect && onSelect(start, end, event.allDay, event.view.type);
      }}
      dateClick={readOnly ? undefined : (event) => onDayClick && onDayClick(toMoment(event.date), event)}
      eventClick={readOnly ? undefined : (e) => onEventClick && onEventClick(e.event.id, e.event.extendedProps.item, e)}
      eventChange={readOnly ? undefined : (e) => onEventChange && onEventChange(e.event.id, e.event.extendedProps.item, toMoment(e.event.start).unix(), e.event.end ? toMoment(e.event.end).unix() : undefined)}

      eventContent={event => {
        // Holiday background: greyed cell with the holiday name (FullCalendar renders no title on
        // background events). Everything else keeps the caller's renderer or the default look.
        if (event.event.display === 'background' && event.event.classNames.includes('calendar-holiday')) {
          return <div className="calendar-holiday-label">{event.event.title}</div>;
        }
        return renderEventContent ? renderEventContent(event) : true;
      }}
      {...rest}
    />
  );

  // FullCalendar only re-measures on WINDOW resize. Inside an animating drawer/overlay (or a tab
  // that becomes visible) it initializes against a zero/partial-width container and renders a
  // crushed grid. Re-measure whenever the container itself changes size.
  return <ContainerSizeSync calendarRef={ref as RefObject<FullCalendar | null>}>{calendar}</ContainerSizeSync>;
});

const ContainerSizeSync = memo((props: { calendarRef: RefObject<FullCalendar | null>; children: React.ReactNode }) => {
  const { calendarRef, children } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => calendarRef.current?.getApi().updateSize());
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [calendarRef]);

  return <div ref={containerRef}>{children}</div>;
});
